import type { AdjustmentState, ForecastAdjustment, ReviewAction, ReviewDecision } from '@/domain';
import { ACTION_TO_STATE, canTransition } from '@/domain';
import { mockStore } from '@/lib/mock-store';
import { getSupabase, isMockBackend } from '@/lib/supabase';
import { auditService } from './auditService';

function randomId(prefix = 'dec'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

interface TransitionInput {
  adjustmentId: string;
  action: ReviewAction;
  actor: string;
  note?: string;
}

interface TransitionResult {
  adjustment: ForecastAdjustment;
  decision: ReviewDecision;
}

interface BulkResult {
  applied: TransitionResult[];
  skipped: { adjustmentId: string; reason: string }[];
}

interface EditQtyInput {
  adjustmentId: string;
  newQty: number;
  actor: string;
  note?: string;
}

export const reviewService = {
  async listDecisions(adjustmentId: string): Promise<ReviewDecision[]> {
    if (isMockBackend) {
      return mockStore
        .getAll('review_decisions')
        .filter((d) => d.adjustment_id === adjustmentId)
        .sort((a, b) => b.decided_at.localeCompare(a.decided_at));
    }
    const sb = getSupabase()!;
    const { data, error } = await sb
      .from('review_decisions')
      .select('*')
      .eq('adjustment_id', adjustmentId)
      .order('decided_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ReviewDecision[];
  },

  async listAllDecisions(): Promise<ReviewDecision[]> {
    if (isMockBackend) {
      return mockStore
        .getAll('review_decisions')
        .slice()
        .sort((a, b) => b.decided_at.localeCompare(a.decided_at));
    }
    const sb = getSupabase()!;
    const { data, error } = await sb
      .from('review_decisions')
      .select('*')
      .order('decided_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ReviewDecision[];
  },

  async transition(input: TransitionInput): Promise<TransitionResult> {
    const all = mockStore.getAll('forecast_adjustments');
    const adj = all.find((a) => a.id === input.adjustmentId);
    if (!adj) throw new Error(`Adjustment not found: ${input.adjustmentId}`);
    const toState = ACTION_TO_STATE[input.action];
    if (!canTransition(adj.state, toState)) {
      throw new Error(
        `Invalid transition: ${adj.state} → ${toState} (via ${input.action})`,
      );
    }

    const decision: ReviewDecision = {
      id: randomId(),
      adjustment_id: adj.id,
      from_state: adj.state,
      to_state: toState,
      action: input.action,
      decided_by: input.actor,
      decided_at: new Date().toISOString(),
      note: input.note ?? '',
    };
    const updated: ForecastAdjustment = { ...adj, state: toState };

    if (isMockBackend) {
      mockStore.update('forecast_adjustments', (r) => r.id === adj.id, { state: toState });
      mockStore.append('review_decisions', decision);
    } else {
      const sb = getSupabase()!;
      await sb
        .from('forecast_adjustments')
        .update({ state: toState })
        .eq('id', adj.id);
      await sb.from('review_decisions').insert(decision);
    }

    await auditService.log({
      actor: input.actor,
      action: 'STATE_TRANSITION',
      entity_type: 'forecast_adjustment',
      entity_id: adj.id,
      summary: `${input.action} điều chỉnh ${adj.id.slice(-6)} · ${decision.from_state} → ${decision.to_state}`,
      payload: {
        adjustment_id: adj.id,
        from: decision.from_state,
        to: decision.to_state,
        action: input.action,
        warehouse_id: adj.warehouse_id,
        customer_id: adj.customer_id,
        date: adj.date,
        qty: adj.qty,
      },
    });

    return { adjustment: updated, decision };
  },

  async transitionBulk(
    inputs: TransitionInput[],
  ): Promise<BulkResult> {
    const applied: TransitionResult[] = [];
    const skipped: BulkResult['skipped'] = [];
    for (const inp of inputs) {
      try {
        const r = await this.transition(inp);
        applied.push(r);
      } catch (e) {
        skipped.push({ adjustmentId: inp.adjustmentId, reason: (e as Error).message });
      }
    }
    return { applied, skipped };
  },

  /**
   * Inline-edit qty of an adjustment in NEW/AM_REVIEWED state.
   * Resets to NEW if it was AM_REVIEWED (planner needs to re-review).
   */
  async editQty(input: EditQtyInput): Promise<ForecastAdjustment> {
    const all = mockStore.getAll('forecast_adjustments');
    const adj = all.find((a) => a.id === input.adjustmentId);
    if (!adj) throw new Error(`Adjustment not found: ${input.adjustmentId}`);
    if (adj.state === 'ACCEPTED') {
      throw new Error('Đã chốt — không sửa qty được. Reopen trước.');
    }
    const next: AdjustmentState = adj.state === 'AM_REVIEWED' ? 'NEW' : adj.state;
    const patch = { qty: input.newQty, state: next };

    if (isMockBackend) {
      mockStore.update('forecast_adjustments', (r) => r.id === adj.id, patch);
    } else {
      const sb = getSupabase()!;
      await sb.from('forecast_adjustments').update(patch).eq('id', adj.id);
    }

    await auditService.log({
      actor: input.actor,
      action: 'UPDATE',
      entity_type: 'forecast_adjustment',
      entity_id: adj.id,
      summary: `Sửa qty điều chỉnh ${adj.id.slice(-6)}: ${adj.qty} → ${input.newQty}` +
        (next !== adj.state ? ` (reset về ${next})` : ''),
      payload: {
        adjustment_id: adj.id,
        from_qty: adj.qty,
        to_qty: input.newQty,
        from_state: adj.state,
        to_state: next,
        note: input.note ?? '',
      },
    });

    return { ...adj, ...patch };
  },
};
